// src/pages/financeiro/AssinaturaStatusModal.js
import React, { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import { FaCheck, FaRegCopy } from 'react-icons/fa';
import ModalContainer from '../../components/modalForm/ModalContainer';
import ModalHeader from '../../components/modalForm/ModalHeader';
import ModalBody from '../../components/modalForm/ModalBody';
import ModalFooter from '../../components/modalForm/ModalFooter';
import { buscarPixAssinatura } from '../../services/financeiro/AssinaturaService';
import { formatCurrency } from '../../utils/numberUtils';

const AssinaturaStatusModal = ({ show, cobrancaStatus, onClose, onLogout }) => {
  const [showPixModal, setShowPixModal] = useState(false);
  const [payloadPix, setPayloadPix] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [erroPix, setErroPix] = useState('');
  const [loadingPix, setLoadingPix] = useState(false);
  const [copiado, setCopiado] = useState(false);

  const status = cobrancaStatus?.status;
  const tokenPix = cobrancaStatus?.tokenPix;
  const valorFatura = cobrancaStatus?.valorFatura;
  const bloqueado = status === 'Bloqueado';

  const mensagem = useMemo(() => {
    if (status === 'Bloqueado') {
      return 'Sistema bloqueado! Realize o pagamento para desbloquear.';
    }

    return 'Pagamento em atraso, para evitar bloqueio realize o pagamento da fatura!';
  }, [status]);

  useEffect(() => {
    if (!show) {
      setShowPixModal(false);
      setPayloadPix('');
      setQrCode('');
      setErroPix('');
      setLoadingPix(false);
      setCopiado(false);
    }
  }, [show]);

  const gerarPix = async () => {
    if (!tokenPix) {
      setErroPix('Não foi possível localizar a fatura para gerar o Pix.');
      setShowPixModal(true);
      return;
    }

    setShowPixModal(true);
    setLoadingPix(true);
    setErroPix('');
    setCopiado(false);

    try {
      const data = await buscarPixAssinatura(tokenPix);
      const dataUrl = await QRCode.toDataURL(data.payload, {
        width: 280,
        margin: 2,
      });

      setPayloadPix(data.payload);
      setQrCode(dataUrl);
    } catch (error) {
      console.error('Erro ao gerar QR Code Pix:', error);
      setErroPix('Não foi possível gerar o QR Code Pix.');
      setPayloadPix('');
      setQrCode('');
    } finally {
      setLoadingPix(false);
    }
  };

  const copiarPix = async () => {
    if (!payloadPix) {
      return;
    }

    await navigator.clipboard.writeText(payloadPix);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 1500);
  };

  const fecharStatus = () => {
    if (bloqueado && onLogout) {
      onLogout();
      return;
    }

    onClose();
  };

  return (
    <>
      <ModalContainer show={show} largura="520px" zIndex={1060}>
        <ModalHeader>
          <h5 className="modal-title">{bloqueado ? 'Sistema Bloqueado' : 'Pagamento em Atraso'}</h5>
          {!bloqueado && (
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
          )}
        </ModalHeader>
        <ModalBody>
          <div className={`alert ${bloqueado ? 'alert-danger' : 'alert-warning'} mb-3`}>
            {mensagem}
          </div>
          {valorFatura != null && (
            <p className="mb-0">
              <strong>Valor da fatura:</strong> {formatCurrency(valorFatura)}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <div className="btn-group">
            <button type="button" className="btn btn-success" onClick={gerarPix}>
              Gerar Pix
            </button>
            <button type="button" className="btn btn-secondary" onClick={fecharStatus}>
              {bloqueado ? 'Sair' : 'Fechar'}
            </button>
          </div>
        </ModalFooter>
      </ModalContainer>

      <ModalContainer show={showPixModal} largura="430px" zIndex={1070}>
        <ModalHeader>
          <h5 className="modal-title">Pagamento Pix</h5>
          <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowPixModal(false)} />
        </ModalHeader>
        <ModalBody>
          <div
            className="text-center"
            style={{
              maxHeight: 'calc(100dvh - 190px)',
              overflowY: 'auto',
              overflowX: 'hidden',
              WebkitOverflowScrolling: 'touch',
              padding: '2px 4px 8px',
            }}
          >
            <p style={{ marginBottom: 18, color: '#5b667a', fontSize: 15 }}>
              Escaneie o QR Code ou copie o código Pix.
            </p>

            {valorFatura != null && (
              <p
                className="mb-3"
                style={{
                  padding: '12px 14px',
                  borderRadius: 12,
                  background: '#eef4f8',
                  color: '#26364f',
                }}
              >
                <strong>Valor:</strong> {formatCurrency(valorFatura)}
              </p>
            )}

            {erroPix && <div className="alert alert-danger">{erroPix}</div>}

            {loadingPix && (
              <div className="login-loading">
                <div className="spinner-border" role="status" style={{ color: '#3d6df2' }} />
                <strong className="login-loading-text">Gerando QR Code...</strong>
              </div>
            )}

            {!loadingPix && qrCode && (
              <>
                <img
                  src={qrCode}
                  alt="QR Code Pix"
                  style={{ width: 'min(280px, 100%)', height: 'auto' }}
                />
                <label className="form-label mt-3 w-100 text-center fw-bold">Pix copia e cola</label>
                <textarea
                  className="form-control"
                  value={payloadPix}
                  readOnly
                  rows={4}
                  style={{
                    resize: 'none',
                    textAlign: 'center',
                    fontSize: 14,
                    lineHeight: 1.55,
                    border: 'none',
                    borderRadius: 12,
                    color: '#1d2c43',
                    background: '#eef4f8',
                    boxShadow: 'none',
                  }}
                />
                {copiado && <div className="text-success fw-bold mt-2">Código Pix copiado!</div>}
              </>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 8,
              width: '100%',
            }}
          >
            <button
              type="button"
              className={`btn ${copiado ? 'btn-success' : 'btn-primary'}`}
              onClick={copiarPix}
              disabled={!payloadPix}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                minWidth: 160,
                fontWeight: 700,
              }}
            >
              {copiado ? <FaCheck /> : <FaRegCopy />}
              {copiado ? 'Copiado' : 'Copiar Pix'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setShowPixModal(false)}>
              Fechar
            </button>
          </div>
        </ModalFooter>
      </ModalContainer>
    </>
  );
};

export default AssinaturaStatusModal;
